import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FaEye, FaInstagram, FaPen, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import api, { addDays, formatDateTime, formatPhone, isoDate } from '../services/api';
import ChatModal from '../components/ChatModal';
import CustomerProfileModal from '../components/CustomerProfileModal';
import RecordingModal from '../components/RecordingModal';
import { BranchTag, useEngagementScope } from '../context/EngagementScope';
import BranchSelect from '../components/BranchSelect';
import {
  AppointmentStatus,
  ChipRow,
  DateRange,
  INSTAGRAM_COLOR,
  InstagramButton,
  InterestTag,
  InterventionBadge,
  LeadFlag,
  NO_SHOW_STAGE,
  STAGES,
  Loader,
  Modal,
  PageHead,
  Pagination,
  SearchBox,
  SourceBadge,
  Spinner,
  ToggleChip,
  UnassignedPill,
  VoiceButton,
  WhatsAppButton,
  contactLabel,
  exportCSV,
  instagramHandle,
  inDateRange,
  usePagination,
} from '../components/ui';

const VIA_CHIPS = [
  { key: '', label: 'All' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'voice', label: 'Voice Call' },
  { key: 'walk_in', label: 'Walk-in / desk' },
  { key: 'checkin', label: 'Salon visit' },
];
const VIA_LABEL = { whatsapp: 'WhatsApp', instagram: 'Instagram', voice: 'Voice call', walk_in: 'Walk-in / desk', checkin: 'Salon visit' };
const ENTRY_CHIPS = [
  { key: '', label: 'All arrivals' },
  { key: 'ads', label: 'From an ad' },
  { key: 'direct', label: 'Direct' },
  { key: 'instagram_post', label: 'Instagram post' },
];

function adLabel(lead) {
  if (lead.entry_type === 'ads' && lead.ad_code) return `${lead.ad_code} · ${lead.ad_name || ''}`.trim();
  if (lead.entry_type === 'ads') return lead.ad_headline ? `From ad · ${lead.ad_headline}` : lead.ad_id ? `From ad · ${lead.ad_id}` : 'From ad';
  if (lead.entry_type === 'instagram_post') return 'Instagram post';
  if (lead.entry_type === 'direct') return 'Direct';
  return '';
}
const DESK_COLOR = '#8a6d3b';
// Leads also show No-show: a lead whose every appointment was missed did not really convert.
const LEAD_STAGES = [...STAGES, NO_SHOW_STAGE];
const STATUS_CHIPS = [{ key: '', label: 'All statuses' }, ...LEAD_STAGES.map((s) => ({ key: s.key, label: s.label }))];
const CSV_COLUMNS = [
  { label: 'Name', key: 'name' },
  { label: 'Phone', key: 'phone' },
  { label: 'Instagram', value: (r) => instagramHandle(r.instagram_username) },
  { label: 'Status', value: (r) => (LEAD_STAGES.find((s) => s.key === r.stage) || STAGES[1]).label },
  { label: 'Services', value: (r) => (r.services || []).join(', ') },
  { label: 'Source', value: (r) => VIA_LABEL[r.via] || 'WhatsApp' },
  { label: 'Ad', value: (r) => adLabel(r) },
  { label: 'Branch', value: (r) => (r.queue ? 'HQ queue' : r.branch_name || '') },
  { label: 'Last activity', key: 'last_at' },
  { label: 'Remarks', key: 'note' },
];

function MiniStat({ label, value, color, active, onClick, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: '#fff',
        border: `1px solid ${active ? color : 'var(--de-border)'}`,
        boxShadow: active ? `0 0 0 1px ${color}` : 'none',
        borderRadius: 10,
        padding: '12px 16px',
        flex: '1 1 110px',
        minWidth: 100,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      <div className="de-row" style={{ gap: 5, fontSize: 11, color: 'var(--de-soft)', marginTop: 3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {icon}
        {label}
      </div>
    </button>
  );
}

function MiniBar({ label, value, max, color, active, onClick }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', background: 'transparent', border: `1px solid ${active ? color : 'transparent'}`, borderRadius: 8, padding: 6, marginBottom: 6, cursor: 'pointer', textAlign: 'left' }}
    >
      <div className="de-row de-row--between" style={{ marginBottom: 3, fontSize: 12 }}>
        <span>{label}</span>
        <span className="de-strong">{value}</span>
      </div>
      <div style={{ background: 'var(--de-border)', borderRadius: 4, height: 7, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
    </button>
  );
}

function Analytics({ leads, filters, apply }) {
  const total = leads.length;
  const whatsapp = leads.filter((l) => l.via === 'whatsapp').length;
  const voice = leads.filter((l) => l.via === 'voice').length;
  const insta = leads.filter((l) => l.via === 'instagram').length;
  const walkIn = leads.filter((l) => l.via === 'walk_in').length;
  const salonVisit = leads.filter((l) => l.via === 'checkin').length;
  const channelMax = Math.max(whatsapp, insta, voice, walkIn, salonVisit);
  const byStage = LEAD_STAGES.map((s) => ({ ...s, value: leads.filter((l) => l.stage === s.key).length }));
  const stageMax = Math.max(1, ...byStage.map((s) => s.value));
  const human = leads.filter((l) => l.handoff_open).length;
  const today = isoDate();
  const days = Array.from({ length: 28 }, (_, i) => addDays(today, i - 27));
  const byDay = days.map((d) => ({ date: d, count: leads.filter((l) => String(l.created_at).slice(0, 10) === d).length }));
  const maxDay = Math.max(1, ...byDay.map((d) => d.count));

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="de-row de-row--wrap" style={{ gap: 8, marginBottom: 12 }}>
        <MiniStat label="Total leads" value={total} color="var(--de-purple)" active={!filters.via && !filters.status} onClick={() => apply({ via: '', status: '' })} />
        <MiniStat label="WhatsApp" value={whatsapp} color="#1fa855" icon={<FaWhatsapp size={12} />} active={filters.via === 'whatsapp'} onClick={() => apply({ via: 'whatsapp' })} />
        <MiniStat label="Instagram" value={insta} color={INSTAGRAM_COLOR} icon={<FaInstagram size={12} />} active={filters.via === 'instagram'} onClick={() => apply({ via: 'instagram' })} />
        <MiniStat label="Voice" value={voice} color="#6b4c9a" icon={<FaPhoneAlt size={10} />} active={filters.via === 'voice'} onClick={() => apply({ via: 'voice' })} />
        <MiniStat label="Walk-in" value={walkIn} color={DESK_COLOR} active={filters.via === 'walk_in'} onClick={() => apply({ via: 'walk_in' })} />
        <MiniStat label="Salon visit" value={salonVisit} color={DESK_COLOR} active={filters.via === 'checkin'} onClick={() => apply({ via: 'checkin' })} />
        {byStage.map((s) => (
          <MiniStat key={s.key} label={s.label} value={s.value} color={s.color} active={filters.status === s.key} onClick={() => apply({ status: s.key })} />
        ))}
        <MiniStat label="Need a person" value={human} color="var(--de-danger)" active={filters.human} onClick={() => apply({ human: !filters.human })} />
      </div>
      <div className="de-g2 de-g2--auto" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <div className="de-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="de-section-label">By status</div>
          {byStage.map((s) => (
            <MiniBar key={s.key} label={s.label} value={s.value} max={stageMax} color={s.color} active={filters.status === s.key} onClick={() => apply({ status: s.key })} />
          ))}
        </div>
        <div className="de-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="de-section-label">By channel</div>
          <MiniBar label="WhatsApp" value={whatsapp} max={channelMax} color="#25D366" active={filters.via === 'whatsapp'} onClick={() => apply({ via: 'whatsapp' })} />
          <MiniBar label="Instagram" value={insta} max={channelMax} color={INSTAGRAM_COLOR} active={filters.via === 'instagram'} onClick={() => apply({ via: 'instagram' })} />
          <MiniBar label="Voice call" value={voice} max={channelMax} color="#6b4c9a" active={filters.via === 'voice'} onClick={() => apply({ via: 'voice' })} />
          <MiniBar label="Walk-in / desk" value={walkIn} max={channelMax} color={DESK_COLOR} active={filters.via === 'walk_in'} onClick={() => apply({ via: 'walk_in' })} />
          <MiniBar label="Salon visit" value={salonVisit} max={channelMax} color={DESK_COLOR} active={filters.via === 'checkin'} onClick={() => apply({ via: 'checkin' })} />
        </div>
        <div className="de-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="de-section-label">Daily trend · last 28 days</div>
          <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 56 }}>
            {byDay.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.count}`}
                style={{ flex: 1, minWidth: 3, height: `${Math.max(3, Math.round((d.count / maxDay) * 52))}px`, background: 'var(--de-purple-brand)', borderRadius: '2px 2px 0 0', opacity: d.count ? 0.85 : 0.2 }}
              />
            ))}
          </div>
          <div className="de-row de-row--between" style={{ fontSize: 10, color: 'var(--de-soft)', marginTop: 4 }}>
            <span>{days[0].slice(5)}</span>
            <span>{days[days.length - 1].slice(5)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RemarkModal({ lead, onSaved, onClose }) {
  const [text, setText] = useState(lead.note || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.updateLead(lead.id, { note: text });
      toast.success(lead.note ? 'Remark updated' : 'Remark added');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Lead remark" onClose={onClose}>
      <div className="de-small de-muted" style={{ marginBottom: 10 }}>
        {lead.name || 'No name yet'} · {contactLabel(lead.phone, lead.instagram_username)}
      </div>
      <textarea className="de-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note for the team…" autoFocus />
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" onClick={save} disabled={saving}>
          {saving ? <Spinner light /> : null}
          Save remark
        </button>
      </div>
    </Modal>
  );
}

function AssignBranch({ lead, onDone }) {
  const { branches } = useEngagementScope();
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try {
      await api.assignLeadBranch(lead.id, Number(branch));
      toast.success('Lead moved to the branch');
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="de-callout" style={{ marginBottom: 14 }}>
      <div className="de-small" style={{ marginBottom: 8 }}>
        This lead has not chosen a branch yet, so only HQ sees it. Move it to a branch:
      </div>
      <div className="de-row de-row--wrap" style={{ gap: 8, alignItems: 'center' }}>
        <div style={{ minWidth: 240, flex: '1 1 240px' }}>
          <BranchSelect branches={branches} value={branch} onChange={setBranch} placeholder="Choose the branch…" />
        </div>
        <button type="button" className="de-btn de-btn--primary de-btn--sm" disabled={!branch || busy} onClick={assign}>
          {busy ? <Spinner light /> : null}
          Assign to branch
        </button>
      </div>
    </div>
  );
}

export default function Leads() {
  const scope = useEngagementScope();
  const showBranch = scope.multiBranch;
  const [queueOnly, setQueueOnly] = useState(false);
  const [profileFor, setProfileFor] = useState(null);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [via, setVia] = useState('');
  const [entry, setEntry] = useState('');
  const [status, setStatus] = useState('');
  const [human, setHuman] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [chatFor, setChatFor] = useState(null);
  const [recording, setRecording] = useState(null);
  const [remarkFor, setRemarkFor] = useState(null);
  const [details, setDetails] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return Promise.all([api.listLeads(), api.listAppointments(), api.listCalls()])
      .then(([l, a, c]) => {
        setLeads(l);
        setAppointments(a);
        setCalls(c);
      })
      .catch((e) => toast.error(e.message || 'Failed to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (queueOnly && !l.queue) return false;
      if (via && l.via !== via) return false;
      if (entry && l.entry_type !== entry) return false;
      if (status && l.stage !== status) return false;
      if (human && !l.handoff_open) return false;
      if (!inDateRange(l.last_at, dateFrom, dateTo)) return false;
      if (!q) return true;
      return [l.name, l.phone, l.note, l.ad_code, l.ad_name, l.ad_headline, l.ad_id, ...(l.services || [])].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [leads, via, entry, status, human, search, dateFrom, dateTo, queueOnly]);

  const { page, setPage, pageSize, setPageSize, paginated, totalPages, offset } = usePagination(filtered, 25);

  const apply = (next) => {
    if ('via' in next) setVia(next.via);
    if ('status' in next) setStatus(next.status);
    if ('human' in next) setHuman(next.human);
  };

  const playCall = (lead) => {
    const call = calls.find((c) => c.customer_phone === lead.phone);
    if (!call) {
      toast.error('No call record found for this lead');
      return;
    }
    setRecording(call);
  };

  const booked = leads.filter((l) => l.status === 'booked').length;
  const queued = leads.filter((l) => l.queue).length;
  // The lead's own branch only (a customer can be a lead at several branches).
  const customerAppointments = details
    ? appointments.filter((a) => a.customer_id === details.customer_id && (details.queue || a.franchise_id === details.franchise_id))
    : [];

  return (
    <div className="de-fade">
      <PageHead
        title="Leads"
        sub={`${leads.length} lead${leads.length !== 1 ? 's' : ''} · ${booked} booked · from WhatsApp, Instagram, voice calls and walk-ins`}
        action={
          <div className="de-row de-row--wrap">
            <button type="button" className="de-btn de-btn--outline" onClick={() => load(false)}>
              ↻ Refresh
            </button>
            <button type="button" className={`de-btn ${showAnalytics ? 'de-btn--gold' : 'de-btn--outline'}`} onClick={() => setShowAnalytics((v) => !v)}>
              📊 {showAnalytics ? 'Hide Report' : 'Analytics'}
            </button>
            <button
              type="button"
              className="de-btn de-btn--primary"
              onClick={() => {
                if (!filtered.length) return toast.error('No leads to export');
                exportCSV(filtered, CSV_COLUMNS, 'leads');
                return toast.success(`Exported ${filtered.length} leads`);
              }}
            >
              ↓ Export CSV
            </button>
          </div>
        }
      />

      {showAnalytics && !loading ? <Analytics leads={leads} filters={{ via, status, human }} apply={apply} /> : null}

      <ChipRow label="Via" chips={VIA_CHIPS} value={via} onChange={setVia} />
      <ChipRow label="Ad" chips={ENTRY_CHIPS} value={entry} onChange={setEntry} />
      <div className="de-row de-row--wrap" style={{ gap: 10, marginBottom: 10 }}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search by name, phone, service or remark…" style={{ maxWidth: 'none', flex: '1 1 300px' }} />
        <DateRange
          from={dateFrom}
          to={dateTo}
          onFrom={setDateFrom}
          onTo={setDateTo}
          onClear={() => {
            setDateFrom('');
            setDateTo('');
          }}
        />
      </div>
      <div className="de-row de-row--wrap" style={{ marginBottom: 12, gap: 8 }}>
        <ToggleChip active={human} onClick={() => setHuman((v) => !v)}>
          🆘 Human intervention needed
        </ToggleChip>
        {scope.isHQ && queued ? (
          <ToggleChip active={queueOnly} onClick={() => setQueueOnly((v) => !v)}>
            🏷 HQ queue · no branch yet ({queued})
          </ToggleChip>
        ) : null}
      </div>
      <ChipRow chips={STATUS_CHIPS} value={status} onChange={setStatus} style={{ marginBottom: 16 }} />

      {loading ? (
        <Loader />
      ) : (
        <div className="de-table-shell">
          <div className="de-table-wrap">
            <table className="de-table">
              <thead>
                <tr>
                  <th style={{ width: 56, textAlign: 'center' }}>S.No</th>
                  <th>Name</th>
                  <th>Phone / ID</th>
                  <th>Status</th>
                  <th>Services</th>
                  <th>Source</th>
                  <th>Ad</th>
                  {showBranch || queued ? <th>Branch</th> : null}
                  <th>Last activity</th>
                  <th style={{ textAlign: 'right' }}>Conversation</th>
                  <th style={{ textAlign: 'center' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={showBranch || queued ? 11 : 10} className="de-table-empty">
                      No leads found{search || via || entry || status || human ? ' — try adjusting your filters' : ''}.
                    </td>
                  </tr>
                ) : (
                  paginated.map((l, i) => (
                    <tr key={l.id} className="de-row-click" onClick={() => setDetails(l)}>
                      <td className="de-td-center de-td-muted">{offset + i + 1}</td>
                      <td className="de-td-strong">{l.name || <span className="de-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>No name yet</span>}</td>
                      <td className="de-td-mono">{contactLabel(l.phone, l.instagram_username)}</td>
                      <td>
                        <div className="de-row de-row--wrap" style={{ gap: 6 }}>
                          <LeadFlag status={l.stage} reason={l.stage_reason} />
                          {l.stage === 'booked' ? <InterestTag service={l.interest_service} /> : null}
                          {l.handoff_open ? <InterventionBadge compact /> : null}
                        </div>
                      </td>
                      <td style={{ maxWidth: 220 }} className="de-muted">
                        {(l.services || []).length ? (
                          <span title={l.services.join(', ')}>
                            {l.services.slice(0, 2).join(', ')}
                            {l.services.length > 2 ? <span className="de-muted"> +{l.services.length - 2} more</span> : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <SourceBadge source={l.via} showLabel />
                      </td>
                      <td className="de-small" style={{ maxWidth: 220 }}>
                        {adLabel(l) ? <span className="de-pill de-pill--gold de-pill--sm" title={l.ad_id || ''}>{adLabel(l)}</span> : <span className="de-muted">—</span>}
                      </td>
                      {showBranch || queued ? (
                        <td>
                          <BranchTag franchiseId={l.franchise_id} name={l.branch_name} queue={l.queue} />
                        </td>
                      ) : null}
                      <td className="de-td-muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(l.last_at)}
                      </td>
                      <td className="de-td-right" onClick={(e) => e.stopPropagation()}>
                        {l.has_chat && l.via === 'instagram' ? <InstagramButton onClick={() => setChatFor(l)} /> : null}
                        {l.has_chat && l.via !== 'instagram' ? <WhatsAppButton onClick={() => setChatFor(l)} /> : null}
                        {l.has_call ? <VoiceButton onClick={() => playCall(l)} /> : null}
                        {!l.has_chat && !l.has_call ? <span className="de-muted">—</span> : null}
                      </td>
                      <td className="de-td-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="de-icon-action de-icon-action--plain"
                          title={l.note ? `Remark: ${l.note}` : 'Add remark'}
                          onClick={() => setRemarkFor(l)}
                          style={{ color: l.note ? 'var(--de-gold-deep)' : 'var(--de-soft)' }}
                        >
                          {l.note ? <FaEye size={14} /> : <FaPen size={12} />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      )}

      {details ? (
        <Modal title={details.name || contactLabel(details.phone, details.instagram_username)} onClose={() => setDetails(null)} size="xwide">
          <div className="de-row de-row--wrap" style={{ gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--de-border)' }}>
            <SourceBadge source={details.via} showLabel />
            {adLabel(details) ? <span className="de-pill de-pill--gold de-pill--sm" title={details.ad_id || ''}>{adLabel(details)}</span> : null}
            <LeadFlag status={details.stage || details.status} reason={details.stage_reason} />
            <span className="de-mono de-muted">{contactLabel(details.phone, details.instagram_username)}</span>
            <BranchTag franchiseId={details.franchise_id} name={details.branch_name} queue={details.queue} />
            {details.note ? <span className="de-small de-gold-text">Remark: {details.note}</span> : null}
          </div>
          {details.queue && scope.isHQ && scope.canManage ? (
            <AssignBranch
              lead={details}
              onDone={() => {
                setDetails(null);
                load(true);
              }}
            />
          ) : null}
          <div className="de-section-label">Appointment details</div>
          {customerAppointments.length === 0 ? (
            <div className="de-card" style={{ textAlign: 'center', color: 'var(--de-soft)', boxShadow: 'none' }}>
              No appointment history found for this contact.
            </div>
          ) : (
            <div className="de-table-shell">
              <div className="de-table-wrap" style={{ maxHeight: '52vh', overflowY: 'auto' }}>
                <table className="de-table">
                  <thead>
                    <tr>
                      <th>Appointment ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Staff</th>
                      <th>Service</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerAppointments.map((a) => (
                      <tr key={a.id}>
                        <td className="de-td-mono">#{a.id}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.starts_at)}</td>
                        <td>
                          <AppointmentStatus status={a.status} />
                        </td>
                        <td>{a.staff_name || <UnassignedPill />}</td>
                        <td>{a.service_name}</td>
                        <td>
                          <SourceBadge source={a.source} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="de-small de-muted" style={{ padding: '10px 16px', borderTop: '1px solid var(--de-border)' }}>
                {customerAppointments.length} appointment{customerAppointments.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
          <div className="de-row de-row--wrap de-mt-16" style={{ gap: 10 }}>
            {details.has_chat ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setChatFor(details)}>
                <FaWhatsapp color="#25D366" /> Open conversation
              </button>
            ) : null}
            {details.has_call ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => playCall(details)}>
                <FaPhoneAlt color="#6b4c9a" /> Call record
              </button>
            ) : null}
            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setRemarkFor(details)}>
              <FaPen /> {details.note ? 'Edit remark' : 'Add remark'}
            </button>
            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setProfileFor(details.customer_id)}>
              👤 Customer profile
            </button>
          </div>
        </Modal>
      ) : null}

      {chatFor ? (
        <ChatModal
          customerId={chatFor.customer_id}
          channel={chatFor.via === 'instagram' ? 'instagram' : 'whatsapp'}
          conversationId={chatFor.conversation_id || null}
          flag={chatFor.stage}
          onClose={() => setChatFor(null)}
          onChanged={() => load(true)}
        />
      ) : null}
      {recording ? <RecordingModal call={recording} onClose={() => setRecording(null)} /> : null}
      {profileFor ? <CustomerProfileModal customerId={profileFor} onClose={() => setProfileFor(null)} /> : null}
      {remarkFor ? (
        <RemarkModal
          lead={remarkFor}
          onClose={() => setRemarkFor(null)}
          onSaved={() => {
            setRemarkFor(null);
            setDetails(null);
            load(true);
          }}
        />
      ) : null}
    </div>
  );
}
